export function splitPocDateParts(value?: string | null): string[] {
  if (value === null || value === undefined) return [];
  return String(value)
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function hasUnresolvedPocDateConflict(row: PocDateRow): boolean {
  const resolved = resolveClientPocStreams(row);
  if (resolved.mode === 'separate') return false;
  if (resolved.mode === 'unresolved') return true;
  if (row.poc_conflict_resolved_at) return false;
  return row.poc_status === 'CONFLICT_NEEDS_REVIEW';
}

export function toPocDateInputValue(raw: string): string {
  const value = String(raw || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const mdY = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdY) {
    return `${mdY[3]}-${mdY[1].padStart(2, '0')}-${mdY[2].padStart(2, '0')}`;
  }
  return '';
}

export interface PocDateRow {
  id?: string | null;
  poc_start_date?: string | null;
  poc_end_date?: string | null;
  poc_status?: string | null;
  feeding_poc_start_date?: string | null;
  feeding_poc_end_date?: string | null;
  feeding_poc_status?: string | null;
  poc_conflict_resolved_at?: string | null;
}

export type PocStreamName = 'SPEECH' | 'FEEDING';
export type SourcePocStatus = 'ACTIVE' | 'INACTIVE';

export interface ConfirmedFeedingThenSpeech {
  order: 'FEEDING_THEN_SPEECH';
  feedingStatus: SourcePocStatus;
  speechStatus: SourcePocStatus;
}

export interface PocStream {
  stream: PocStreamName;
  poc_start_date: string | null;
  poc_end_date: string | null;
  poc_status: string;
}

export type PocStreamResolution =
  | { mode: 'single'; speech: PocStream }
  | { mode: 'separate'; speech: PocStream; feeding: PocStream }
  | { mode: 'unresolved'; poc_start_date: string | null; poc_end_date: string | null };

/**
 * Historical migration rows that stored feeding first and speech second.
 * Chunk B: Aaron Sieg. Chunk C: Nathaniel Quezada and Dylan Mac Aulay.
 * Sierra Teitelbaum's stored pair matches her feeding source row, then her speech source row.
 * Isaac Ahumada Garcia's stored ends match feeding (inactive), then speech (active); starts were not stored.
 * Any other semicolon pair stays unresolved.
 */
const CONFIRMED_FEEDING_THEN_SPEECH: Record<string, ConfirmedFeedingThenSpeech> = {
  'CLI-N7C1-DE4D': { order: 'FEEDING_THEN_SPEECH', feedingStatus: 'ACTIVE', speechStatus: 'ACTIVE' },
  'CLI-N783-3772': { order: 'FEEDING_THEN_SPEECH', feedingStatus: 'ACTIVE', speechStatus: 'ACTIVE' },
  'CLI-N795-C030': { order: 'FEEDING_THEN_SPEECH', feedingStatus: 'ACTIVE', speechStatus: 'ACTIVE' },
  'CLI-N79I-D567': { order: 'FEEDING_THEN_SPEECH', feedingStatus: 'ACTIVE', speechStatus: 'ACTIVE' },
  'CLI-N79T-D0D6': { order: 'FEEDING_THEN_SPEECH', feedingStatus: 'INACTIVE', speechStatus: 'ACTIVE' }
};

export function confirmedFeedingThenSpeech(clientId?: string | null): ConfirmedFeedingThenSpeech | null {
  if (!clientId) return null;
  return CONFIRMED_FEEDING_THEN_SPEECH[clientId] || null;
}

type PocField =
  | { kind: 'empty' }
  | { kind: 'single'; value: string }
  | { kind: 'pair'; first: string; second: string }
  | { kind: 'ambiguous' };

function rawPocValue(value?: string | null): string | null {
  const text = String(value ?? '').trim();
  return text || null;
}

function classifyPocField(value?: string | null): PocField {
  if (value == null || String(value).trim() === '') return { kind: 'empty' };
  if (!String(value).includes(';')) return { kind: 'single', value: String(value).trim() };
  const parts = splitPocDateParts(value);
  if (parts.length === 2) return { kind: 'pair', first: parts[0], second: parts[1] };
  return { kind: 'ambiguous' };
}

function hasStoredFeedingPlan(row: PocDateRow): boolean {
  return [row.feeding_poc_start_date, row.feeding_poc_end_date, row.feeding_poc_status]
    .some((value) => String(value ?? '').trim() !== '');
}

function planStatus(value?: string | null, fallback = 'ACTIVE'): string {
  const text = String(value ?? '').trim();
  if (!text || text === 'CONFLICT_NEEDS_REVIEW') return fallback;
  return text;
}

function speechDatesStayCombined(start: PocField, end: PocField): boolean {
  return start.kind === 'pair' || start.kind === 'ambiguous' || end.kind === 'pair' || end.kind === 'ambiguous';
}

export function resolveClientPocStreams(
  row: PocDateRow,
  options?: { confirmation?: ConfirmedFeedingThenSpeech | null }
): PocStreamResolution {
  const confirmation = options && Object.prototype.hasOwnProperty.call(options, 'confirmation')
    ? options.confirmation ?? null
    : confirmedFeedingThenSpeech(row.id);
  const start = classifyPocField(row.poc_start_date);
  const end = classifyPocField(row.poc_end_date);
  const singleStatus = String(row.poc_status || 'ACTIVE');

  if (hasStoredFeedingPlan(row) && !speechDatesStayCombined(start, end)) {
    return {
      mode: 'separate',
      feeding: {
        stream: 'FEEDING',
        poc_start_date: rawPocValue(row.feeding_poc_start_date),
        poc_end_date: rawPocValue(row.feeding_poc_end_date),
        poc_status: planStatus(row.feeding_poc_status)
      },
      speech: {
        stream: 'SPEECH',
        poc_start_date: start.kind === 'single' ? start.value : null,
        poc_end_date: end.kind === 'single' ? end.value : null,
        poc_status: planStatus(row.poc_status)
      }
    };
  }

  const asSingle = (): PocStreamResolution => ({
    mode: 'single',
    speech: {
      stream: 'SPEECH',
      poc_start_date: start.kind === 'single' ? start.value : null,
      poc_end_date: end.kind === 'single' ? end.value : null,
      poc_status: singleStatus
    }
  });

  const asUnresolved = (): PocStreamResolution => ({
    mode: 'unresolved',
    poc_start_date: rawPocValue(row.poc_start_date),
    poc_end_date: rawPocValue(row.poc_end_date)
  });

  if (!confirmation) {
    if (start.kind === 'pair' || start.kind === 'ambiguous' || end.kind === 'pair' || end.kind === 'ambiguous') {
      return asUnresolved();
    }
    return asSingle();
  }

  const canSplit = (start.kind === 'pair' || end.kind === 'pair')
    && (start.kind === 'empty' || start.kind === 'pair')
    && (end.kind === 'empty' || end.kind === 'pair');
  if (canSplit) {
    return {
      mode: 'separate',
      feeding: {
        stream: 'FEEDING',
        poc_start_date: start.kind === 'pair' ? start.first : null,
        poc_end_date: end.kind === 'pair' ? end.first : null,
        poc_status: confirmation.feedingStatus
      },
      speech: {
        stream: 'SPEECH',
        poc_start_date: start.kind === 'pair' ? start.second : null,
        poc_end_date: end.kind === 'pair' ? end.second : null,
        poc_status: confirmation.speechStatus
      }
    };
  }

  if (start.kind === 'pair' || end.kind === 'pair' || start.kind === 'ambiguous' || end.kind === 'ambiguous') {
    return asUnresolved();
  }
  return asSingle();
}
